function distanceAbbr(distance) {
  if (distance === 'Kilometers') return 'km';
  if (distance === 'Miles') return 'mi';
  if (distance === 'Hours') return 'hrs';
  return distance || 'mi';
}

function volumeAbbr(volume) {
  if (volume === 'Liters') return 'L';
  if (volume?.startsWith('Gallons')) return 'gal';
  return volume || 'L';
}

// Returns the unit label string for a given format spec + store units
export function getUnit(spec, storeUnits = {}) {
  if (!spec) return '';
  switch (spec.type) {
    case 'currency':              return storeUnits.currency || 'USD';
    case 'currency_per_distance': return `${storeUnits.currency || 'USD'}/${distanceAbbr(storeUnits.distance)}`;
    case 'odometer':              return distanceAbbr(storeUnits.distance);
    case 'efficiency':            return storeUnits.consumption || 'MPG';
    case 'volume':                return volumeAbbr(storeUnits.volume);
    case 'percent':               return '%';
    case 'count':                 return '';
    default:                      return spec.unit || '';
  }
}

// Map currency codes to their natural display locale so compact notation
// uses the correct scale (e.g. en-IN gives "₹49L" instead of "₹4.9M").
const CURRENCY_LOCALE = {
  INR: 'en-IN',
  USD: 'en-US',
  EUR: 'en-DE',
  GBP: 'en-GB',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  AUD: 'en-AU',
  CAD: 'en-CA',
  CHF: 'de-CH',
  BRL: 'pt-BR',
  MXN: 'es-MX',
  ZAR: 'en-ZA',
  AED: 'ar-AE',
  SAR: 'ar-SA',
};

// Returns a locale-formatted string with units baked in
export function formatValue(raw, spec, storeUnits = {}) {
  if (raw == null || raw === '' || isNaN(Number(raw))) return '—';

  const locale = navigator.language || 'en-US';
  const num = Number(raw);

  if (!spec) {
    return new Intl.NumberFormat(locale).format(num);
  }

  switch (spec.type) {
    case 'currency': {
      const curr = storeUnits.currency || 'USD';
      const decimals = spec.decimals ?? 0;
      if (spec.compact && Math.abs(num) > 999999) {
        const compactLocale = CURRENCY_LOCALE[curr] || locale;
        return new Intl.NumberFormat(compactLocale, {
          style: 'currency',
          currency: curr,
          notation: 'compact',
          compactDisplay: 'short',
          maximumFractionDigits: 1,
        }).format(num);
      }
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
    }

    case 'currency_per_distance': {
      const curr = storeUnits.currency || 'USD';
      const dist = distanceAbbr(storeUnits.distance);
      const decimals = spec.decimals ?? 2;
      const money = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
      return `${money}/${dist}`;
    }

    case 'odometer': {
      const unit = distanceAbbr(storeUnits.distance);
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num) + ' ' + unit;
    }

    case 'efficiency': {
      const unit = storeUnits.consumption || 'MPG';
      const decimals = spec.decimals ?? 1;
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num) + ' ' + unit;
    }

    case 'volume': {
      const unit = volumeAbbr(storeUnits.volume);
      const decimals = spec.decimals ?? 1;
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num) + ' ' + unit;
    }

    case 'percent': {
      const decimals = spec.decimals ?? 1;
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num) + '%';
    }

    case 'count':
      if (spec.compact && Math.abs(num) > 999999) {
        return new Intl.NumberFormat(locale, {
          notation: 'compact',
          compactDisplay: 'short',
          maximumFractionDigits: 1,
        }).format(num);
      }
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num);

    case 'number':
    default: {
      const unit = spec.unit ?? '';
      const decimals = spec.decimals ?? 0;
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
      return unit ? `${formatted} ${unit}` : formatted;
    }
  }
}
